/*
 * BNO explore hotspots.
 *
 * Drives the interactive dots over the plugin screenshot in
 * sections/bno-plugin-explore.liquid. Loaded as a deferred module, so the
 * overlay markup is parsed by the time this runs.
 *
 * The overlay ships INERT from Liquid: the container is [hidden] and each dot is
 * [disabled]. If this script never runs (JS off / load error) the image is left
 * with no dead controls. Here we drop both gates and take over.
 *
 * Mobile DOM: the 750px breakpoint is a client-side concern Liquid can't
 * resolve, so every hotspot is rendered and the ones tagged data-mobile="false"
 * are physically REMOVED from the DOM below 750px (not just hidden), then
 * re-inserted in original order when the viewport crosses back above 750px.
 * Removed nodes are not keyboard-focusable.
 *
 * Interaction: hover (hover-capable devices) previews a bubble; click/tap pins
 * it; Enter/Space fire the button's click natively. Only one bubble is open at a
 * time. Outside-click and Escape close it. On open, the bubble is clamped so it
 * never leaves the image horizontally. All motion is CSS; reduced motion is
 * handled entirely there.
 */
(() => {
  if (window.__bnoHotspotsInit) return;
  window.__bnoHotspotsInit = true;

  const containers = document.querySelectorAll('[data-bno-hotspots]');
  if (!containers.length) return;

  const mqMobile = window.matchMedia('(max-width: 749px)');

  containers.forEach((container) => {
    // Ordered, stable list of every hotspot (kept even while detached).
    const items = Array.from(container.querySelectorAll('.bno-hotspot'));
    if (!items.length) return;

    // Drop the inert gate now that JS is driving the overlay.
    container.hidden = false;
    items.forEach((el) => {
      const dot = el.querySelector('.bno-hotspot__dot');
      if (dot) dot.disabled = false;
    });

    const canHover = window.matchMedia('(hover: hover)').matches;

    let current = null; // the open .bno-hotspot, or null
    let pinned = false; // opened by click/tap (sticks past mouseleave)

    // Keep the bubble inside the image horizontally. Uses layout geometry
    // (offsetLeft/offsetWidth), not getBoundingClientRect, so the measurement is
    // unaffected by the open scale animation. The bubble is centred on the dot,
    // so its edges are centreX ± half; push inward if either overflows.
    const clamp = (el) => {
      const bubble = el.querySelector('.bno-hotspot__bubble');
      if (!bubble) return;
      const containerW = container.clientWidth;
      const centreX = el.offsetLeft; // the dot's x within the overlay/image
      const half = bubble.offsetWidth / 2;
      const pad = 8;
      let shift = 0;
      if (centreX - half < pad) {
        shift = pad - (centreX - half);
      } else if (centreX + half > containerW - pad) {
        shift = containerW - pad - (centreX + half);
      }
      bubble.style.setProperty('--bno-hs-shift', Math.round(shift) + 'px');
    };

    const close = () => {
      if (!current) return;
      current.classList.remove('is-open');
      const dot = current.querySelector('.bno-hotspot__dot');
      if (dot) dot.setAttribute('aria-expanded', 'false');
      current = null;
      pinned = false;
    };

    const open = (el, pin) => {
      if (current !== el) close();
      current = el;
      if (pin) pinned = true;
      el.classList.add('is-open');
      const dot = el.querySelector('.bno-hotspot__dot');
      if (dot) dot.setAttribute('aria-expanded', 'true');
      clamp(el);
    };

    items.forEach((el) => {
      const dot = el.querySelector('.bno-hotspot__dot');
      if (!dot) return;

      // Click / tap / Enter / Space (buttons fire click for the latter two):
      // toggle the pinned bubble.
      dot.addEventListener('click', (e) => {
        e.stopPropagation(); // don't let the document handler close it
        if (current === el && pinned) close();
        else open(el, true);
      });

      // Hover preview on hover-capable devices only, so touch (which has no real
      // hover) doesn't double-fire with click.
      if (canHover) {
        el.addEventListener('mouseenter', () => open(el, false));
        el.addEventListener('mouseleave', () => {
          if (!pinned) close();
        });
      }
    });

    // Outside click closes (dot clicks stop propagation; bubble clicks are
    // inside .bno-hotspot, so selecting bubble text won't close it).
    document.addEventListener('click', (e) => {
      if (current && !e.target.closest('.bno-hotspot')) close();
    });

    // Escape closes and returns focus to the dot.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !current) return;
      const dot = current.querySelector('.bno-hotspot__dot');
      close();
      if (dot) dot.focus();
    });

    // Keep the DOM to the right set for the current viewport. Iterating the
    // original order and appendChild-ing each shown node rebuilds correct order;
    // non-mobile nodes are removed outright below 750px.
    const syncViewport = () => {
      close(); // a bubble open on a node about to be removed would dangle
      const mobile = mqMobile.matches;
      items.forEach((el) => {
        const show = mobile ? el.dataset.mobile === 'true' : true;
        if (show) {
          container.appendChild(el);
        } else if (el.parentNode) {
          el.remove();
        }
      });
    };

    // Clamp EVERY hotspot's bubble, not just the open one. A bubble whose dot is
    // near an edge overflows the image even while closed (visibility:hidden still
    // takes layout), which extends the page's scrollable width — an invisible
    // horizontal overflow. Applying --bno-hs-shift to all bubbles up front keeps
    // them inside the image in both states. Only clamp connected nodes (below
    // 750px the non-mobile hotspots are detached).
    const clampAll = () => items.forEach((el) => {
      if (el.isConnected) clamp(el);
    });

    syncViewport();
    clampAll();

    // Re-clamp when the layout changes: the 749px breakpoint (which also
    // re-attaches/detaches hotspots) and any resize (the shift is a pixel offset
    // off the current image width). Throttle resize to one recompute per frame.
    mqMobile.addEventListener('change', () => {
      syncViewport();
      clampAll();
    });

    let clampRaf = 0;
    window.addEventListener('resize', () => {
      if (clampRaf) return;
      clampRaf = requestAnimationFrame(() => {
        clampRaf = 0;
        clampAll();
      });
    });
  });
})();
